/**
 * rago-server ↔ rago-web server-plane DTOs (ARCHITECTURE §4b) — the zod
 * runtime validators behind contracts/openapi/web-server-plane.v1.yaml.
 */
import { z } from "zod";
import { dashIp } from "./directZone.js";
import { reachabilitySchema } from "./broker.js";

const ipAddressSchema = z
  .string()
  .max(45)
  .refine(
    (s) => {
      try {
        dashIp(s);
        return true;
      } catch {
        return false;
      }
    },
    { message: "not an IP address literal" },
  );

export const natMethodSchema = z.enum(["upnp", "natpmp", "manual", "none"]);

export const serverTokenRequestSchema = z.object({
  assertion: z.string().min(1).max(4096),
});

export const serverTokenResponseSchema = z.object({
  accessToken: z.string(),
  expiresInS: z.number().int(),
  serverId: z.uuid(),
  serverHash: z.string(),
});
export type ServerTokenResponse = z.infer<typeof serverTokenResponseSchema>;

export const heartbeatRequestSchema = z.object({
  version: z.string().min(1).max(60),
  // Current friendly name — mirrored into servers.name (claim stays the
  // initial writer). Optional: pre-rename servers omit it.
  name: z.string().trim().min(1).max(120).optional(),
  port: z.number().int().min(1).max(65535),
  // A server with no private IPv4 (a no-NAT VPS) has nothing local to
  // report; tolerate null as well as an omitted field.
  localAddresses: z
    .array(ipAddressSchema)
    .max(8)
    .nullish()
    .transform((v) => v ?? []),
  publicPort: z.number().int().min(1).max(65535).optional(),
  natMethod: natMethodSchema,
  httpsReady: z.boolean(),
  capabilities: z.array(z.string().min(1).max(40)).max(16).default([]),
  // Network prefs (defaults preserve pre-pref servers' behavior exactly).
  httpsRequired: z.boolean().default(true),
  relayEnabled: z.boolean().default(true),
  customConnections: z.array(z.string().min(1).max(200)).max(8).default([]),
});
export type HeartbeatRequest = z.infer<typeof heartbeatRequestSchema>;

export const heartbeatResponseSchema = z.object({
  observedPublicAddress: z.string().nullable(),
  // The broker's stored (or just-refreshed) outside-in verdict — the same
  // shape the owner sees on /v1/resources, plus probedUri for the server.
  reachability: reachabilitySchema,
  relay: z.object({
    assigned: z.boolean(),
    endpoint: z.string().optional(),
  }),
  grantsCursorLatest: z.string(),
  certRenewalDue: z.boolean(),
  certExpiresAt: z.iso.datetime().nullable().optional(),
  // max(watch_state.seq) visible to this server (other servers' rows for its
  // syncing principals); a mismatch with the server's stored cursor nudges
  // a down-sync pull. Omitted by a pre-field cloud.
  watchStateCursorLatest: z.string().optional(),
});
export type HeartbeatResponse = z.infer<typeof heartbeatResponseSchema>;

export const grantLibraryIdsSchema = z.union([
  z.literal("*"),
  z.array(z.number().int().min(0)),
]);

export const grantItemSchema = z.object({
  grantId: z.uuid(),
  userId: z.uuid(),
  userType: z.enum(["owner", "household", "shared"]),
  displayName: z.string(),
  libraryIds: grantLibraryIdsSchema,
  restrictions: z.record(z.string(), z.unknown()),
  revoked: z.boolean(),
  version: z.number().int(),
  // The account's "Sync my watch state" consent, mirrored per grant so the
  // server knows which principals' completed views to upload. Defaulted so
  // pages from a pre-field cloud still parse.
  watchStateSync: z.boolean().default(false),
  // The account behind this grant was deleted: the row is a tombstone
  // (revoked, displayName "") that outlives it so the revocation still
  // syncs. A server may erase what it holds for the principal. Defaulted
  // like watchStateSync.
  accountDeleted: z.boolean().default(false),
});
export type GrantItem = z.infer<typeof grantItemSchema>;

export const grantsPageSchema = z.object({
  items: z.array(grantItemSchema),
  nextCursor: z.string().nullable(),
  // max(seq) for the server, computed before the page rows are read; the
  // server persists it as its cursor once the drain hits an empty page, so
  // the heartbeat's grantsCursorLatest comparison goes quiet.
  cursorLatest: z.string(),
});
export type GrantsPage = z.infer<typeof grantsPageSchema>;

export const certificateRequestSchema = z.object({
  csr: z.string().min(1).max(16384),
});

export const certificateResponseSchema = z.object({
  certChain: z.string(),
  expiresAt: z.iso.datetime(),
  serial: z.string(),
});
export type CertificateResponse = z.infer<typeof certificateResponseSchema>;

/**
 * Body of POST /v1/servers/{id}/relay. A server that measured the relay nodes
 * names the one it reaches fastest; a build from before that choice sends an
 * empty body and is placed by the broker alone.
 */
export const relayRequestSchema = z.object({
  prefer: z
    .string()
    .max(60)
    .regex(/^relay-[a-z0-9-]+$/)
    .optional(),
});
export type RelayRequest = z.infer<typeof relayRequestSchema>;

export const relayAssignmentSchema = z.object({
  relayEndpoint: z.string(),
  relayToken: z.string(),
  expiresAt: z.iso.datetime(),
  /** Every live node, the assigned one included, for the server to measure. */
  nodes: z.array(z.object({ id: z.string(), endpoint: z.string() })),
});
export type RelayAssignment = z.infer<typeof relayAssignmentSchema>;

export const relayNodeStatusSchema = z.object({
  label: z.string().min(1).max(60),
  publicIp: ipAddressSchema,
  clientPort: z.number().int().min(1).max(65535),
  tunnelHost: z.string().min(1).max(255),
  tunnelPort: z.number().int().min(1).max(65535),
  version: z.string().max(60).optional(),
  status: z.enum(["online", "draining"]),
  activeTunnels: z.number().int().min(0),
});
export type RelayNodeStatus = z.infer<typeof relayNodeStatusSchema>;

export const relayUsageWindowSchema = z.object({
  serverHash: z.string().regex(/^[a-z2-7]{26}$/),
  bytesIn: z.number().int().min(0),
  bytesOut: z.number().int().min(0),
  windowStart: z.iso.datetime(),
  windowEnd: z.iso.datetime(),
});

export const relayUsageBatchSchema = z.array(relayUsageWindowSchema).max(500);
